import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ReviewFormBuilder } from './review-form.builder';
import { IReviewFormSubmit } from './review-form.data';
import { IFormFieldErrorMessages } from '../form/form-master.interface';
import { Subject } from 'rxjs';
import { IconSizesEnum, IIcon } from '../icon/icon.component';
import { takeUntil } from 'rxjs/operators';
import {
  I2gNgTrackingService,
  ITrackingObject,
  I2gNgTrackingActionBuilder,
} from 'i2g-ng-tracking';
import { List } from 'immutable';
import { Rating } from './models/rating-item.model';
import { FormService } from '../../services/form/form.service';

/**
 * Currently this form is used by Client Portal and Business Web
 * TODO: move this out from i2g-ng-ui to Business Web once Client Portal open Business Web for leaving a review
 */

@Component({
  selector: 'i2g-review-form',
  templateUrl: 'review-form.component.html',
  styleUrls: ['review-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [ReviewFormBuilder],
})
export class ReviewFormComponent implements OnInit, OnDestroy {
  /**
   * company logo URL
   */
  @Input() companyLogoUrl: string;

  /**
   * company name
   */
  @Input() companyName: string;

  /**
   * page title
   */
  @Input() title;

  /**
   * list of ratings
   */
  @Input() ratings: List<Rating>;

  /**
   * review rating error messages
   */
  @Input() ratingErrorMessages: IFormFieldErrorMessages;

  /**
   * review comment section title
   */
  @Input() commentSectionTitle;

  /**
   * placeholder for review comment
   */
  @Input() commentPlaceholder;

  /**
   * review comment error messages
   */
  @Input() commentErrorMessages: IFormFieldErrorMessages;

  /**
   * review comment label
   */
  @Input() commentLabel;

  /**
   * submit button label
   */
  @Input() buttonLabel;

  /**
   * submit processing label
   */
  @Input() processingLabel;

  /**
   * submit is in progress
   */
  @Input() isSubmitProcessing = false;

  /**
   * Tracking purpose
   */
  @Input() trackingObject: ITrackingObject;

  /**
   * terms condition
   */
  @Input() termsConditionsLabel =
    'By submitting, you agree to the <a class="underline" href="https://support.2go.com/hc/en-us/articles/360049112751" target="_blank" rel="nofollow">Terms of use</a>';

  /**
   * form submitted event
   */
  @Output() submitForm = new EventEmitter<IReviewFormSubmit>();

  form: FormGroup;
  formKeys: any;
  spinnerIcon: IIcon;
  commentMaxLength = 4000;

  private _destroyed = new Subject<boolean>();

  constructor(
    private _builder: ReviewFormBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingService: I2gNgTrackingService,
    private _formService: FormService
  ) {}

  ngOnInit() {
    this.formKeys = this._builder.FORM_KEYS;
    this.form = this._builder.buildForm(this.ratings, this.commentMaxLength);
    this.spinnerIcon = {
      name: 'dotted-spinner',
      size: IconSizesEnum.custom,
    };

    this.ratings.forEach((rating) => {
      this.form
        .get(rating.id)
        .valueChanges.pipe(takeUntil(this._destroyed))
        .subscribe((value) => {
          const trackingAction = this._trackingActionBuilder.buildInputChanged();
          this._trackingService.trackEvent(
            this.trackingObject,
            trackingAction,
            {
              rating: value,
              category: rating.id,
            }
          );
        });
    });
  }

  onSubmitForm() {
    this._formService.markControlsInFormGroupDirty(this.form);

    if (this.form.valid) {
      this.submitForm.emit(this.form.value);
    }
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  getRatingErrorMessage(rating: Rating) {
    const field = this.form.get(rating.id);

    if (field.hasError('min')) {
      return this.ratingErrorMessages['min'].message;
    }

    return '';
  }
}
